import { useUserState } from '../../redux/reducers/userReducer';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import {
    Avatar,
    Body1,
    Button,
    Persona,
    makeStyles,
    mergeClasses,
    tokens,
} from '@fluentui/react-components';
import { Dismiss16Regular, StarEmphasisFilled } from '@fluentui/react-icons';
import { useCommonStyles } from '../common/Styles/commonStyles';
import { Column, Row } from '../common';
import { BrandCard } from '../common/BrandCard';
import { ConfirmationDialog } from '../dialogs/ConfirmationDialog';
import { Contact, ControlSize, Individual, Person, Phone, User } from '../../data/types';

import { useUtilities } from '../../utilities/useUtilities';

const useStyles = makeStyles({
    horizontalCardImage: {
        width: '36px',
        height: '36px',
    },
    caption: {
        color: tokens.colorNeutralForeground3,
    },
    subtextColumn: {
        gap: '0',
    },
});
export type PersonControlProps = {
    className?: string;
    person?: Person;
    size?: ControlSize;
    selectable?: boolean;
    onSelectionChange?: (selPerson: Person, selected: boolean) => void;
    onRemove?: (person: Person) => void;
    showStar?: boolean;
    confirmDelete?: boolean;
};
export const PersonControl = (props: PersonControlProps) => {
    const {
        className,
        person,
        size = 'small',
        selectable,
        onSelectionChange,
        onRemove,
        confirmDelete,
        showStar = false,
    } = props;

    //Vars
    const isUser = !!(person as User)?.userId;
    const isContact = !isUser;
    const asIndividual = person as Individual;
    const asContact = person as Contact;
    const asUser = person as User;

    //Hooks
    const { users } = useUserState();
    const utils = useUtilities();

    //State
    const [isSelected, setIsSelected] = useState<boolean>(false);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const cardClasses = mergeClasses(className);

    //Logic
    const cityStateZip = () => {
        if (
            !!asIndividual?.address?.city ||
            !!asIndividual?.address?.state ||
            !!asIndividual?.address?.zip ||
            !!asIndividual?.address?.country
        )
            return `${asIndividual?.address?.city || ''}, ${asIndividual?.address?.state || ''} ${asIndividual?.address?.zip || ''}`;
        else return '';
    };
    const firstPrimaryPhone = (): Phone | undefined => {
        if (isContact) {
            return asContact?.phone?.find((x) => x.primary === true);
        } else {
            return asUser?.phone;
        }
    };
    const firstNonPrimaryPhone = (): Phone | undefined => {
        if (isContact) {
            return asContact?.phone?.find((x) => x.primary === false);
        } else {
            return undefined;
        }
    };
    const getPrimaryPhoneText = () => {
        const primPhone = firstPrimaryPhone();
        return !!primPhone?.type ? `${primPhone.type}: ${primPhone?.number}` : '';
    };
    const getFirstNonPrimaryPhoneText = () => {
        const primPhone = firstNonPrimaryPhone();
        return !!primPhone?.type ? `${primPhone?.type}: ${primPhone?.number}` : '';
    };
    const getName = () => {
        if (isUser) return utils.getUsersName(asUser.userId, users || []);
        else return person?.name || '';
    };

    //UI Events
    const onSelectedChange = (_: any, state: any) => {
        setIsSelected(state.selected || state.checked);
        !!onSelectionChange && onSelectionChange(person as Person, state.selected || state.checked);
    };
    const clickRemovePerson = (e: any, person: Person) => {
        e.stopPropagation();

        !!onRemove && onRemove(person);
    };

    return (
        <>
            {size === 'large' && (
                <BrandCard
                    className={cardClasses}
                    title={getName()}
                    description={asIndividual?.title || asUser?.username}
                    headerIcon={
                        <Avatar
                            name={getName()}
                            color='colorful'
                            idForColor={getName() ? getName()[0] : ''}
                            shape='circular'
                            className={styles.horizontalCardImage}
                            badge={showStar ? { icon: <StarEmphasisFilled /> } : undefined}
                        />
                    }
                    maxWidth='350px'
                    minWidth='320px'
                    backgroundColor={tokens.colorNeutralCardBackground}
                    selected={!!selectable ? isSelected : undefined}
                    onSelectionChange={!!selectable ? onSelectedChange : undefined}
                    action={
                        <>
                            {!!onRemove && !!person && (
                                <>
                                    {!!confirmDelete ? (
                                        <ConfirmationDialog
                                            title='Delete?'
                                            text='Are you sure you want to remove this person?'
                                            onConfirm={(e: any) => clickRemovePerson(e, person)}
                                        >
                                            <Button
                                                className={commonStyles.button.root}
                                                appearance='transparent'
                                                icon={<Dismiss16Regular />}
                                                data-testid='person-control-delete-button'
                                            />
                                        </ConfirmationDialog>
                                    ) : (
                                        <Button
                                            className={commonStyles.button.root}
                                            appearance='transparent'
                                            icon={<Dismiss16Regular />}
                                            onClick={(e: any) => clickRemovePerson(e, person)}
                                            data-testid='person-control-delete-button'
                                        />
                                    )}
                                </>
                            )}
                        </>
                    }
                >
                    <Row padding='pt-3'>
                        <Column className={styles.subtextColumn}>
                            <Body1 className={styles.caption}>{asIndividual?.address?.address1}</Body1>
                            <Body1 className={styles.caption}>{asContact?.address?.address2}</Body1>
                            <Body1 className={styles.caption}>{cityStateZip()}</Body1>
                            {isUser && (
                                <>
                                    {!!firstPrimaryPhone() && (
                                        <Body1 className={styles.caption}>{getPrimaryPhoneText()}</Body1>
                                    )}
                                </>
                            )}
                        </Column>
                        {isContact && (
                            <Column className={styles.subtextColumn} align='right'>
                                {!!firstPrimaryPhone() && <Body1 className={styles.caption}>{getPrimaryPhoneText()}</Body1>}
                                {!!firstNonPrimaryPhone() && (
                                    <Body1 className={styles.caption}>{getFirstNonPrimaryPhoneText()}</Body1>
                                )}
                            </Column>
                        )}
                    </Row>
                </BrandCard>
            )}

            {size === 'medium' && (
                <Row noWrap>
                    <Column>
                        <Persona
                            name={getName()}
                            textAlignment='start'
                            size='large'
                            avatar={{
                                color: 'colorful',
                                idForColor: getName() ? getName()[0] : '',
                                badge: showStar ? { icon: <StarEmphasisFilled /> } : undefined,
                            }}
                            secondaryText={asIndividual?.title}
                            tertiaryText={asIndividual?.email || asUser?.username}
                            quaternaryText={getPrimaryPhoneText()}
                        />
                    </Column>
                    <Column width='auto'>
                        {!!onRemove && !!person && (
                            <>
                                {!!confirmDelete ? (
                                    <ConfirmationDialog
                                        title='Delete?'
                                        text='Are you sure you want to remove this person?'
                                        onConfirm={(e: any) => clickRemovePerson(e, person)}
                                    >
                                        <Button
                                            className={commonStyles.button.root}
                                            appearance='transparent'
                                            icon={<Dismiss16Regular />}
                                            data-testid='person-control-delete-button'
                                        />
                                    </ConfirmationDialog>
                                ) : (
                                    <Button
                                        className={commonStyles.button.root}
                                        appearance='transparent'
                                        icon={<Dismiss16Regular />}
                                        onClick={(e: any) => clickRemovePerson(e, person)}
                                        data-testid='person-control-delete-button'
                                    />
                                )}
                            </>
                        )}
                    </Column>
                </Row>
            )}

            {size === 'mediumCard' && (
                <BrandCard
                    className={cardClasses}
                    maxWidth='350px'
                    minWidth='280px'
                    backgroundColor={tokens.colorNeutralCardBackground}
                    selected={!!selectable ? isSelected : undefined}
                    onSelectionChange={!!selectable ? onSelectedChange : undefined}
                >
                    <Row noWrap>
                        <Column>
                            <Persona
                                name={getName()}
                                size='large'
                                avatar={{
                                    color: 'colorful',
                                    idForColor: getName() ? getName()[0] : '',
                                    badge: showStar ? { icon: <StarEmphasisFilled /> } : undefined,
                                }}
                                secondaryText={asIndividual?.title}
                                tertiaryText={asIndividual?.email || asUser?.username}
                                quaternaryText={getPrimaryPhoneText()}
                            />
                        </Column>
                        <Column width='auto'>
                            {!!onRemove && !!person && (
                                <>
                                    {!!confirmDelete ? (
                                        <ConfirmationDialog
                                            title='Delete?'
                                            text='Are you sure you want to remove this person?'
                                            onConfirm={(e: any) => clickRemovePerson(e, person)}
                                        >
                                            <Button
                                                className={commonStyles.button.root}
                                                appearance='transparent'
                                                icon={<Dismiss16Regular />}
                                                data-testid='person-control-delete-button'
                                            />
                                        </ConfirmationDialog>
                                    ) : (
                                        <Button
                                            className={commonStyles.button.root}
                                            appearance='transparent'
                                            icon={<Dismiss16Regular />}
                                            onClick={(e: any) => clickRemovePerson(e, person)}
                                            data-testid='person-control-delete-button'
                                        />
                                    )}
                                </>
                            )}
                        </Column>
                    </Row>
                </BrandCard>
            )}

            {size === 'small' && (
                <Row>
                    <Column>
                        <Persona
                            name={getName()}
                            textAlignment='start'
                            size='medium'
                            avatar={{
                                color: 'colorful',
                                idForColor: getName() ? getName()[0] : '',
                                badge: showStar ? { icon: <StarEmphasisFilled /> } : undefined,
                            }}
                            secondaryText={asIndividual?.email || asUser?.username}
                        />
                    </Column>
                    <Column width='auto'>
                        {!!onRemove && !!person && (
                            <>
                                {!!confirmDelete ? (
                                    <ConfirmationDialog
                                        title='Delete?'
                                        text='Are you sure you want to remove this person?'
                                        onConfirm={(e: any) => clickRemovePerson(e, person)}
                                    >
                                        <Button
                                            className={commonStyles.button.root}
                                            appearance='transparent'
                                            icon={<Dismiss16Regular />}
                                            data-testid='person-control-delete-button'
                                        />
                                    </ConfirmationDialog>
                                ) : (
                                    <Button
                                        className={commonStyles.button.root}
                                        appearance='transparent'
                                        icon={<Dismiss16Regular />}
                                        onClick={(e: any) => clickRemovePerson(e, person)}
                                        data-testid='person-control-delete-button'
                                    />
                                )}
                            </>
                        )}
                    </Column>
                </Row>
            )}

            {size === 'smallCard' && (
                <BrandCard
                    className={cardClasses}
                    maxWidth='350px'
                    minWidth='300px'
                    backgroundColor={tokens.colorNeutralCardBackground}
                    selected={!!selectable ? isSelected : undefined}
                    onSelectionChange={!!selectable ? onSelectedChange : undefined}
                >
                    <Row noWrap>
                        <Column>
                            <Persona
                                name={getName()}
                                textAlignment='start'
                                size='medium'
                                avatar={{
                                    color: 'colorful',
                                    idForColor: getName() ? getName()[0] : '',
                                    badge: showStar ? { icon: <StarEmphasisFilled /> } : undefined,
                                }}
                                secondaryText={asIndividual?.email || asUser?.username}
                            />
                        </Column>
                        <Column width='auto'>
                            {!!onRemove && !!person && (
                                <>
                                    {!!confirmDelete ? (
                                        <ConfirmationDialog
                                            title='Delete?'
                                            text='Are you sure you want to remove this person?'
                                            onConfirm={(e: any) => clickRemovePerson(e, person)}
                                        >
                                            <Button
                                                className={commonStyles.button.root}
                                                appearance='transparent'
                                                icon={<Dismiss16Regular />}
                                                data-testid='person-control-delete-button'
                                            />
                                        </ConfirmationDialog>
                                    ) : (
                                        <Button
                                            className={commonStyles.button.root}
                                            appearance='transparent'
                                            icon={<Dismiss16Regular />}
                                            onClick={(e: any) => clickRemovePerson(e, person)}
                                            data-testid='person-control-delete-button'
                                        />
                                    )}
                                </>
                            )}
                        </Column>
                    </Row>
                </BrandCard>
            )}

            {size === 'option' && (
                <Persona
                    name={getName()}
                    size='small'
                    textAlignment='center'
                    avatar={{
                        color: 'colorful',
                        'aria-hidden': true,
                        shape: 'circular',
                        idForColor: getName() ? getName()[0] : '',
                        badge: showStar ? { icon: <StarEmphasisFilled /> } : undefined,
                    }}
                />
            )}

            {size === 'small-option' && (
                <Persona
                    name={getName()}
                    size='extra-small'
                    textAlignment='center'
                    avatar={{
                        color: 'colorful',
                        'aria-hidden': true,
                        shape: 'circular',
                        idForColor: getName() ? getName()[0] : '',
                        badge: showStar ? { icon: <StarEmphasisFilled /> } : undefined,
                    }}
                />
            )}
        </>
    );
};
